import {
  createFileUploadStore,
  FileUploadState,
  FileUploadStoreOptions,
} from '@common/uploads/uploader/file-upload-store';
import {
  persistActiveUploads,
  restorePersistedUploads,
} from '@common/uploads/uploader/persist-uploads';
import {toast} from '@shadcn/toast/toast';
import {Trans} from '@ui/i18n/trans';
import {useSettings} from '@ui/settings/use-settings';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import {useStoreWithEqualityFn} from 'zustand/traditional';

export type FileUploadStore = ReturnType<typeof createFileUploadStore>;
export const FileUploadContext = createContext<FileUploadStore>(null!);

export function useFileUploadStore<T>(
  selector: (s: FileUploadState) => T,
  equalityFn?: (left: T, right: T) => boolean,
): T {
  const store = useContext(FileUploadContext);
  return useStoreWithEqualityFn(store, selector, equalityFn);
}

export function useFileUploadStoreApi(): FileUploadStore {
  return useContext(FileUploadContext);
}

export interface FileUploadProviderProps {
  children: ReactNode;
  options?: FileUploadStoreOptions;
  // Use an existing (global) store instead of creating one for this subtree.
  store?: FileUploadStore;
}
export function FileUploadProvider({
  children,
  options,
  store: externalStore,
}: FileUploadProviderProps) {
  const settings = useSettings();

  const [store] = useState(() => {
    return externalStore ?? createFileUploadStore({settings, options});
  });

  // A global store manages its own lifecycle (see GlobalUploadHost).
  useUploadStoreLifecycle(
    externalStore ? null : store,
    options?.persistUploads ? (options.persistKey ?? 'default') : null,
  );

  return (
    <FileUploadContext.Provider value={store}>
      {children}
    </FileUploadContext.Provider>
  );
}

/**
 * Restore interrupted uploads, persist queue metadata, warn before leaving
 * while uploads run, and auto-pause offline / resume online.
 */
export function useUploadStoreLifecycle(
  store: FileUploadStore | null,
  persistKey: string | null,
) {
  useEffect(() => {
    if (!store || !persistKey) {
      return;
    }

    const restored = restorePersistedUploads(persistKey);
    if (restored.length) {
      store.getState().restoreUploads(restored);
      store.getState().onRestored?.(restored.length);
    }

    let timer = 0;
    let lastUploads = store.getState().fileUploads;
    const unsub = store.subscribe(state => {
      if (state.fileUploads === lastUploads) return;
      lastUploads = state.fileUploads;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        persistActiveUploads(store.getState(), persistKey);
      }, 500);
    });

    const persistNow = () => {
      window.clearTimeout(timer);
      persistActiveUploads(store.getState(), persistKey);
    };

    const onLeave = (e: BeforeUnloadEvent) => {
      persistNow();
      if (store.getState().activeUploadsCount > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    const onHide = () => {
      if (document.visibilityState === 'hidden') {
        persistNow();
      }
    };
    window.addEventListener('beforeunload', onLeave);
    window.addEventListener('pagehide', persistNow);
    document.addEventListener('visibilitychange', onHide);

    const onOffline = () => {
      const state = store.getState();
      state.fileUploads.forEach((upload, id) => {
        if (upload.status === 'inProgress' || upload.status === 'pending') {
          // Auto-pause: resumes by itself when the connection returns.
          state.pauseUpload(id, false);
        }
      });
    };
    const onOnline = () => {
      const state = store.getState();
      let resumed = 0;
      state.fileUploads.forEach((upload, id) => {
        if (
          (upload.status === 'aborted' && !upload.pausedByUser) ||
          (upload.status === 'failed' && !upload.errorMessage)
        ) {
          state.retryUpload(id);
          resumed += 1;
        }
      });
      if (resumed) {
        toast.success(
          <Trans
            message="[one Back online. Resuming 1 upload.|other Back online. Resuming :count uploads.]"
            values={{count: resumed}}
          />,
        );
      }
    };
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);

    return () => {
      unsub();
      persistNow();
      window.removeEventListener('beforeunload', onLeave);
      window.removeEventListener('pagehide', persistNow);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, [store, persistKey]);
}
